import { useTranslation } from 'react-i18next';
import { RequestStatus } from '@magimp/shared';
import { Avatar, Icon, useInteractiveRow } from '../../../ui/index.js';
import { NotificationDot } from '../../notifications/index.js';
import { formatRequestDensity } from '../density.js';
import { formatRequestDestination } from '../destination-display.js';
import { formatRelative, formatSLA, formatVolume, formatWeight } from '../format.js';
import { formatRequestOrigin } from '../origin-display.js';
import { RequestSelectionCheckbox } from '../selection/RequestSelectionCheckbox.js';
import type { RequestView } from '../types.js';
import { CarrierResponsesBar } from './CarrierResponsesBar.js';
import { RequestListThumbnail, RequestListThumbnailPlaceholder } from './RequestListThumbnail.js';
import { RequestStatusPill } from './RequestStatusPill.js';
import { UnreadBadge } from './UnreadBadge.js';

// WHY: this role-scoped view is frozen at the pre-#2750 desktop layout for
// `logist_cn`; future list features land in the default view only.
export function ClassicRequestListRow({
  r,
  now,
  selected,
  selectionMode = false,
  selectionChecked = false,
  onSelect,
  onToggleSelection = () => undefined,
  unread,
  notificationCount,
}: {
  r: RequestView;
  now: number;
  selected: boolean;
  selectionMode?: boolean;
  selectionChecked?: boolean;
  onSelect: () => void;
  onToggleSelection?: (shiftKey: boolean) => void;
  unread: { unreadCount: number; isMarkedUnread: boolean };
  notificationCount?: number;
}) {
  const { t } = useTranslation('requests');
  const { background, handlers } = useInteractiveRow({ selected, onActivate: onSelect });
  const sla = formatSLA(
    {
      status: r.status,
      slaDeadline: r.slaDeadline,
      inWorkAt: r.inWorkAt,
      closedAt: r.closedAt,
      processedAt: r.processedAt,
    },
    now,
  );
  const archived = r.status === RequestStatus.ARCHIVE;
  const opacity = archived ? 0.75 : 1;
  const thumbnailItem = r.items.find((item) => item.photoKeys.length > 0);

  const cellStyle = (firstColumn: boolean, extra: React.CSSProperties = {}) => ({
    padding: '0 10px',
    height: 42,
    display: 'flex',
    alignItems: 'center',
    borderBottom: '1px solid var(--border-2)',
    background,
    cursor: 'pointer',
    transition: 'background var(--dur-1) var(--ease-out)',
    boxShadow: selected && firstColumn ? 'inset 3px 0 0 var(--accent-1)' : 'none',
    ...extra,
  });

  return (
    <>
      {selectionMode && (
        <div style={cellStyle(true, { justifyContent: 'center', cursor: 'default' })}>
          <RequestSelectionCheckbox
            checked={selectionChecked}
            label={t('export.bulk.selectRequest', { id: r.id })}
            onToggle={onToggleSelection}
          />
        </div>
      )}
      <div style={cellStyle(!selectionMode, { opacity })} {...handlers}>
        <span style={{ fontFamily: 'var(--font-mono)', fontSize: 12, color: 'var(--fg-2)' }}>
          {r.id}
        </span>
      </div>
      <div style={cellStyle(false, { opacity, minWidth: 0, gap: 8 })} {...handlers}>
        {thumbnailItem?.photoKeys[0] ? (
          <RequestListThumbnail
            requestId={r.id}
            itemId={thumbnailItem.id}
            objectKey={thumbnailItem.photoKeys[0]}
          />
        ) : (
          <RequestListThumbnailPlaceholder />
        )}
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
            minWidth: 0,
            flex: '1 1 0',
          }}
        >
          <div
            style={{
              color: 'var(--fg-1)',
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              fontWeight: 500,
              minWidth: 0,
            }}
          >
            <span
              title={r.name}
              style={{
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                minWidth: 0,
              }}
            >
              {r.name}
            </span>
            {r.kind === 'CONTAINER' && (
              <span
                data-testid="classic-request-container-badge"
                style={{
                  flex: 'none',
                  fontSize: 10,
                  lineHeight: 1,
                  fontWeight: 500,
                  padding: '2px 6px',
                  borderRadius: 'var(--r-1)',
                  background: 'var(--bg-3)',
                  color: 'var(--fg-2)',
                  border: '1px solid var(--border-1)',
                  whiteSpace: 'nowrap',
                }}
              >
                {t('list.row.containerBadge')}
              </span>
            )}
            {r.kind === 'SIMPLE' && (
              <span
                data-testid="classic-request-simple-badge"
                style={{
                  flex: 'none',
                  fontSize: 10,
                  lineHeight: 1,
                  fontWeight: 500,
                  padding: '2px 6px',
                  borderRadius: 'var(--r-1)',
                  background: 'var(--bg-3)',
                  color: 'var(--fg-2)',
                  border: '1px solid var(--border-1)',
                  whiteSpace: 'nowrap',
                }}
              >
                {t('list.row.simpleBadge')}
              </span>
            )}
          </div>
          <div
            className="small"
            data-testid="classic-request-route-line"
            style={{ display: 'flex', alignItems: 'center', gap: 5, minWidth: 0 }}
          >
            <Icon name="arrow-right" size={11} />
            <span
              style={{
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                minWidth: 0,
              }}
            >
              {formatRequestOrigin(r, t('draftDefaults.from'))} →{' '}
              {r.kind === 'CONTAINER'
                ? t('list.row.containerDestination')
                : formatRequestDestination(r, { short: false })}
              {r.items.length > 0 ? ` · ${r.items.length} ${t('common.items')}` : ''}
            </span>
          </div>
        </div>
        <UnreadBadge unreadCount={unread.unreadCount} isMarkedUnread={unread.isMarkedUnread} />
        {notificationCount !== undefined && notificationCount > 0 ? (
          <NotificationDot count={notificationCount} />
        ) : null}
      </div>
      <div style={cellStyle(false)} {...handlers}>
        <RequestStatusPill status={r.status} />
      </div>
      <div style={cellStyle(false)} {...handlers}>
        <CarrierResponsesBar carriers={r.carriers} />
      </div>
      <div style={cellStyle(false, { justifyContent: 'flex-end' })} {...handlers}>
        <span
          data-testid="classic-request-weight"
          style={{
            fontVariantNumeric: 'tabular-nums',
            color: r.weight == null ? 'var(--fg-4)' : 'var(--fg-1)',
            fontSize: 12,
          }}
        >
          {formatWeight(r.weight)}
        </span>
      </div>
      <div style={cellStyle(false, { justifyContent: 'flex-end' })} {...handlers}>
        <span
          data-testid="classic-request-volume"
          style={{
            fontVariantNumeric: 'tabular-nums',
            color: r.volume == null ? 'var(--fg-4)' : 'var(--fg-1)',
            fontSize: 12,
          }}
        >
          {formatVolume(r.volume)}
        </span>
      </div>
      <div style={cellStyle(false, { justifyContent: 'flex-end' })} {...handlers}>
        <span
          data-testid="classic-request-density"
          style={{ fontVariantNumeric: 'tabular-nums', color: 'var(--fg-1)', fontSize: 12 }}
        >
          {formatRequestDensity(r.weight, r.volume)}
        </span>
      </div>
      <div
        style={cellStyle(false, { opacity, minWidth: 0 })}
        data-sla-level={sla.level}
        {...handlers}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0 }}>
          {sla.level === 'none' ? (
            <span style={{ color: 'var(--fg-4)', fontSize: 12 }}>—</span>
          ) : (
            <span
              data-testid="classic-sla-primary"
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 5,
                fontSize: 12,
                fontVariantNumeric: 'tabular-nums',
                fontWeight: sla.level === 'ok' ? 400 : 500,
                whiteSpace: 'nowrap',
                color:
                  sla.level === 'err'
                    ? 'var(--status-error-fg)'
                    : sla.level === 'warn'
                      ? 'var(--status-warn-fg)'
                      : sla.level === 'success'
                        ? 'var(--status-success-fg)'
                        : 'var(--fg-2)',
              }}
            >
              <Icon name={sla.level === 'err' ? 'timer-off' : 'timer'} size={12} />
              {sla.text}
            </span>
          )}
          <div data-testid="classic-request-created" style={{ fontSize: 11, color: 'var(--fg-4)' }}>
            {formatRelative(r.createdAt, now)}
          </div>
        </div>
      </div>
      <div style={cellStyle(false, { opacity, minWidth: 0 })} {...handlers}>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            color: 'var(--fg-2)',
            fontSize: 12,
            minWidth: 0,
            width: '100%',
          }}
        >
          <Avatar name={r.manager.name} size={20} />
          <span
            title={r.manager.name}
            style={{
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              minWidth: 0,
              flex: '1 1 0',
            }}
          >
            {r.manager.name}
          </span>
        </div>
      </div>
      <div style={cellStyle(false)} {...handlers}>
        <span
          data-testid="classic-request-updated"
          style={{
            fontSize: 12,
            color: 'var(--fg-3)',
            fontVariantNumeric: 'tabular-nums',
          }}
        >
          {formatRelative(r.updatedAt, now)}
        </span>
      </div>
    </>
  );
}
